import React, { useState } from 'react';
import { exportExcel } from '../utils/api';

export default function StepReport({ client, keywordGroups: initialGroups, onBack, onPriorityUpdate, onDone, savedCampaign }) {
  const [groups, setGroups] = useState(initialGroups.map(g => ({
    ...g,
    keywords: g.keywords.map(k => ({ ...k, priority: k.priority || 'Unset' }))
  })));

  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('volume');

  const updatePriority = (groupIndex, kwIndex, priority) => {
    setGroups(prev => {
      const updated = prev.map((g, gi) => {
        if (gi !== groupIndex) return g;
        return {
          ...g,
          keywords: g.keywords.map((k, ki) => {
            if (ki !== kwIndex) return k;
            if (onPriorityUpdate) onPriorityUpdate(k.keyword, priority);
            return { ...k, priority };
          })
        };
      });
      return updated;
    });
  };

  // Set all keywords in a group to the same priority
  const setGroupPriority = (groupIndex, priority) => {
    setGroups(prev => prev.map((g, gi) => {
      if (gi !== groupIndex) return g;
      return { ...g, keywords: g.keywords.map(k => ({ ...k, priority })) };
    }));
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      await exportExcel(client, groups);
      setExported(true);
    } catch (e) {
      setError('Export failed — please try again.');
    }
    setExporting(false);
  };

  const allKeywords = groups.flatMap(g => g.keywords);
  const totalKeywords = allKeywords.length;
  const highCount = allKeywords.filter(k => k.priority === 'High').length;
  const avgVolume = Math.round(allKeywords.reduce((a, k) => a + (k.searchVolume || 0), 0) / (totalKeywords || 1));
  const ranked = allKeywords.filter(k => typeof k.rank === 'number' && k.rank <= 100).length;

  const priorityOrder = { High: 0, Medium: 1, Low: 2, Unset: 3 };

  const sortKeywords = (keywords) => {
    return [...keywords].sort((a, b) => {
      if (sortBy === 'volume') return (b.searchVolume || 0) - (a.searchVolume || 0);
      if (sortBy === 'rank') {
        const ra = typeof a.rank === 'number' ? a.rank : 999;
        const rb = typeof b.rank === 'number' ? b.rank : 999;
        return ra - rb;
      }
      if (sortBy === 'priority') return (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3);
      return a.keyword.localeCompare(b.keyword);
    });
  };

  const filterKeywords = (keywords) => {
    if (filter === 'high') return keywords.filter(k => k.priority === 'High');
    if (filter === 'medium') return keywords.filter(k => k.priority === 'Medium');
    if (filter === 'ranked') return keywords.filter(k => typeof k.rank === 'number' && k.rank <= 100);
    if (filter === 'volume') return keywords.filter(k => (k.searchVolume || 0) >= 10);
    return keywords;
  };

  const priorityBadgeClass = (p) => {
    if (p === 'High') return 'badge-high';
    if (p === 'Medium') return 'badge-medium';
    if (p === 'Low') return 'badge-low';
    return 'badge-low';
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Keyword research report</div>
        <div className="page-sub">Set priority for each keyword, then download the Excel report.</div>
      </div>

      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total keywords</div>
          <div className="metric-value">{totalKeywords.toLocaleString()}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Keyword groups</div>
          <div className="metric-value">{groups.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">High priority</div>
          <div className="metric-value" style={{ color: 'var(--green)' }}>{highCount}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Avg. monthly searches</div>
          <div className="metric-value">{avgVolume.toLocaleString()}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Currently ranking</div>
          <div className="metric-value">{ranked}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Filter:</label>
            <select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 'auto' }}>
              <option value="all">All keywords</option>
              <option value="high">High priority</option>
              <option value="medium">Medium priority</option>
              <option value="ranked">Currently ranking (top 100)</option>
              <option value="volume">Has search volume (10+)</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Sort by:</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ width: 'auto' }}>
              <option value="volume">Search volume</option>
              <option value="rank">Current rank</option>
              <option value="priority">Priority</option>
              <option value="alpha">Alphabetical</option>
            </select>
          </div>
        </div>
      </div>

      {groups.map((group, gi) => {
        const filtered = filterKeywords(sortKeywords(group.keywords));
        if (!filtered.length) return null;

        return (
          <div className="card" key={gi} style={{ marginBottom: 12, padding: '1rem 1.25rem' }}>
            <div className="group-row">
              <div className="group-name">{group.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Set all:</span>
                {['High', 'Medium', 'Low'].map(p => (
                  <button
                    key={p}
                    className="btn btn-sm"
                    onClick={() => setGroupPriority(gi, p)}
                    style={{ fontSize: 11, padding: '3px 10px' }}
                  >
                    {p}
                  </button>
                ))}
                <span style={{ fontSize: 12, color: 'var(--gray-500)', marginLeft: 8 }}>
                  {filtered.length} keywords
                </span>
              </div>
            </div>

            <table className="kw-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th>Location</th>
                  <th className="num">Monthly searches</th>
                  <th className="num">Current rank</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((kw, i) => {
                  const rankDisplay = typeof kw.rank === 'number' && kw.rank <= 100 ? kw.rank : '100+';
                  // Find actual index in original group.keywords for update
                  const originalIndex = group.keywords.indexOf(kw);
                  return (
                    <tr key={i}>
                      <td>{kw.keyword}</td>
                      <td style={{ color: 'var(--gray-500)' }}>{kw.location}</td>
                      <td className="num">{(kw.searchVolume || 0).toLocaleString()}</td>
                      <td className="num">{rankDisplay}</td>
                      <td>
                        <select
                          value={kw.priority || 'Unset'}
                          onChange={e => updatePriority(gi, originalIndex, e.target.value)}
                          style={{
                            width: 'auto',
                            fontSize: 12,
                            padding: '3px 8px',
                            borderRadius: 'var(--radius-md)',
                            borderColor: kw.priority === 'High'
                              ? '#97C459'
                              : kw.priority === 'Medium'
                              ? '#f0c040'
                              : 'var(--gray-300)',
                            background: kw.priority === 'High'
                              ? 'var(--green-light)'
                              : kw.priority === 'Medium'
                              ? 'var(--amber-light)'
                              : '#fff',
                            color: kw.priority === 'High'
                              ? 'var(--green)'
                              : kw.priority === 'Medium'
                              ? 'var(--amber)'
                              : 'var(--gray-700)',
                            fontWeight: 500
                          }}
                        >
                          <option value="Unset">— Unset</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {error && <div className="alert alert-error">{error}</div>}
      {exported && <div className="alert alert-success">Excel file downloaded successfully.</div>}

      <div className="actions">
        <button className="btn" onClick={onBack}>← Back</button>
        {onDone && (
          <button className="btn" onClick={onDone}>
            {savedCampaign ? '← Back to campaigns' : 'Save & return to client'}
          </button>
        )}
        <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
          {exporting
            ? <><span className="spinner" /> Generating Excel…</>
            : 'Download Excel report'}
        </button>
      </div>
    </div>
  );
}
